import { appConfig } from "@matcha/app-config";

/**
 * The ADMIN Cognito pool and the ADMIN API, both distinct from the ones the
 * app uses.
 *
 * That separation is the whole security story of this console: the two pools
 * have different issuers, so a token minted for somebody using Find Me
 * Matcha cannot authenticate here. The admin HTTP API's JWT authorizer is
 * bound to this pool, and `requireAdminAuth` in the backend rejects a
 * mismatched `iss` a second time.
 *
 * Values come from `@matcha/app-config` for the active `APP_ENV` rather than
 * being written here, so a pool that moves does not leave a stale literal
 * behind in a file nobody re-reads.
 *
 * Override for local work against a real stack in `.env.local`:
 *   VITE_ADMIN_COGNITO_USER_POOL_ID=...
 *   VITE_ADMIN_COGNITO_USER_POOL_CLIENT_ID=...
 *   VITE_ADMIN_API_URL=http://localhost:4100
 */
export const ADMIN_COGNITO_USER_POOL_ID =
  import.meta.env.VITE_ADMIN_COGNITO_USER_POOL_ID ?? appConfig.admin.userPoolId;

export const ADMIN_COGNITO_USER_POOL_CLIENT_ID =
  import.meta.env.VITE_ADMIN_COGNITO_USER_POOL_CLIENT_ID ??
  appConfig.admin.userPoolClientId;

export const ADMIN_API_URL =
  import.meta.env.VITE_ADMIN_API_URL ?? appConfig.admin.apiUrl;

/**
 * The local bypass.
 *
 * `pnpm dev` in `apps/admin-backend` sets `ALLOW_DEV_AUTH_HEADERS=1` and
 * trusts `x-user-sub`; this flag is the console's half of that arrangement.
 * Double-gated on `import.meta.env.DEV` so it cannot survive a production
 * build even if the variable is set — a shipped bundle that authenticates
 * with a header is the one mistake this console cannot afford.
 */
export const DEV_AUTH =
  import.meta.env.DEV && import.meta.env.VITE_ADMIN_DEV_AUTH === "1";

/** The sub the dev bypass presents. A real uuid — every character is hex,
 *  because `admin_audit_log.admin_sub` is a uuid column and Postgres will
 *  not take a near-miss. Matches `DEV_ADMIN_SUB` in the backend. */
export const DEV_ADMIN_SUB = "00000000-0000-4000-8000-00000000ad11";
export const DEV_ADMIN_EMAIL = "dev@findmematcha.local";

/**
 * False when this build has no admin pool configured and no dev bypass.
 * Amplify with an empty `userPoolId` does not fail at configure time — it
 * fails deep inside the first auth call, where it reads as "sign-in is
 * broken" rather than "this build has no pool".
 */
export const isAdminAuthConfigured =
  DEV_AUTH ||
  (ADMIN_COGNITO_USER_POOL_ID !== "" &&
    ADMIN_COGNITO_USER_POOL_CLIENT_ID !== "");

export const amplifyAdminConfig = {
  Auth: {
    Cognito: {
      userPoolId: ADMIN_COGNITO_USER_POOL_ID,
      userPoolClientId: ADMIN_COGNITO_USER_POOL_CLIENT_ID,
      loginWith: { email: true as const },
      // Invite-only. There is no sign-up surface in this app at all, and the
      // pool refuses self sign-up regardless — accounts are minted with
      // `aws cognito-idp admin-create-user`. See docs/ADMIN_CONSOLE.md.
      signUpVerificationMethod: "code" as const,
    },
  },
} as const;
